<script setup lang="ts">
import { ref } from 'vue'
import { useFirebaseStore } from '@/stores/firebase'
import HealthStatus from '@/components/HealthCheck.vue' // Import the health status component
import type { ClickOutsideBinding } from '@/types/all'

const firebaseStore = useFirebaseStore()
const showUserMenu = ref(false)
const notificationCount = ref(2) // Example notification count
const userName = ref(firebaseStore.userDisplayName)

// Emit for sidebar toggle
const emit = defineEmits(['toggle-sidebar'])

const toggleSidebar = () => {
  emit('toggle-sidebar')
}

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

// Close menu when clicking outside
const closeUserMenu = () => {
  if (showUserMenu.value) {
    showUserMenu.value = false
  }
}

const handleSignOut = () => {
  firebaseStore.signOut()
}

// Custom click-outside directive
const vClickOutside = {
  mounted(el: HTMLElement, binding: ClickOutsideBinding) {
    el.clickOutsideEvent = function (event: Event) {
      if (!(el === event.target || el.contains(event.target as Node))) {
        binding.value(event, el)
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el: HTMLElement) {
    if (el.clickOutsideEvent) {
      document.removeEventListener('click', el.clickOutsideEvent)
    }
  },
}
</script>

<template>
  <header
    id="mainNavBar"
    class="h-14 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200 shadow-md flex items-center z-10"
  >
    <!-- Mobile sidebar toggle -->
    <button
      @click="toggleSidebar"
      class="md:hidden p-2 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none mr-2"
      aria-label="Toggle sidebar"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-5 w-5"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
          clip-rule="evenodd"
        />
      </svg>
    </button>

    <!-- Spacer to push the following items to the right -->
    <div class="flex-grow"></div>

    <!-- Right side actions -->
    <div class="flex items-center space-x-4">
      <!-- Search trigger -->
      <button
        class="hidden p-2 text-gray-500 hover:bg-gray-100 rounded-full focus:outline-none"
        aria-label="Search"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clip-rule="evenodd"
          />
        </svg>
      </button>

      <!-- Health Status Component -->
      <HealthStatus />

      <!-- Notifications -->
      <button
        class="hidden p-2 text-gray-500 hover:bg-gray-100 rounded-full focus:outline-none relative"
        aria-label="Notifications"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
          />
        </svg>
        <span
          v-if="notificationCount > 0"
          class="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full transform translate-x-1 -translate-y-1"
        >
          {{ notificationCount }}
        </span>
      </button>

      <!-- User profile dropdown -->
      <div class="relative" v-click-outside="closeUserMenu">
        <button
          @click="toggleUserMenu"
          class="cursor-pointer flex items-center focus:outline-none"
          aria-expanded="false"
        >
          <span
            class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 overflow-hidden"
          >
            <!-- If user has avatar image -->
            <!-- <img src="path/to/avatar.jpg" alt="User avatar" class="h-full w-full object-cover" /> -->
            <!-- Default avatar with initials -->
            <span class="font-medium text-sm">{{ userName?.charAt(0) || '#' }}</span>
          </span>
        </button>

        <!-- Dropdown menu -->
        <div
          v-show="showUserMenu"
          class="absolute right-0 mt-2 w-48 py-1 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
        >
          <div class="px-4 py-2 text-xs text-gray-500">Signed in as</div>
          <div class="px-4 py-1 text-sm font-medium">{{ userName }}</div>
          <div class="border-t border-gray-100 my-1"></div>
          <a href="#" class="hidden block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >Your Profile</a
          >
          <a href="#" class="hidden block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >Settings</a
          >
          <div class="hidden border-t border-gray-100 my-1"></div>
          <a
            @click.prevent="handleSignOut()"
            class="cursor-pointer block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Sign out
          </a>
        </div>
      </div>
    </div>
  </header>
</template>
